import { ArrowUturnLeftIcon, PaperClipIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AdmittedPatientsModal from "../ipd/admission/AdmittedPatientsModal";
import CommonButton from "../common11/Buttons/CommonButton";
import PrintButton from "../common11/Buttons/PrintButton";
import CommonDatePicker from "../common11/formInputs/CommonDatePicker";
import CommonTimePicker from "../common11/formInputs/CommonTimePicker";
import InputField from "../common11/formInputs/InputField";
import InputAreaField from "../common11/formInputs/InputAreaField";
import SpeechToTextArea from "../common11/formInputs/SpeechToTextArea";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import { showErrorToast, showSuccessToast } from "../common11/toastUtils";
import {
  listAdmittedPatientsForDoctorRound,
  mapWebPatientToAdmissionRow,
} from "../../services/doctorRoundService";
import { formatDisplayTime, toLookupOption } from "../../services/otNoteService";
import {
  fetchOtLookups,
  fetchOtNoteById,
  fetchOtNotesForVisit,
  fetchOtTables,
  resetOtNote,
  saveOtNote,
  setOperationDetailsId,
  setOtReservationContext,
  setSelectedPatient as setSelectedPatientInStore,
} from "../../store/slices/otNoteSlice";
import OpdQueueListingModal from "./OpdQueueListingModal";
import OTReservationListingModal from "./OTReservationListingModal";
import { applyOtNoteDetailToForm, buildOtNoteSavePayload } from "./otNoteFormMappers";

const OT_SURGEON_OPTIONS = [
  { id: 1, value: 1, label: "Dr. Shinde" },
  { id: 2, value: 2, label: "Dr. Patil" },
];
const OT_ANAESTHETIST_OPTIONS = [
  { id: 3, value: 3, label: "Dr. Joshi" },
  { id: 4, value: 4, label: "Dr. Deshmukh" },
];
const OT_ASST_DOC_OPTIONS = [
  {
    id: 202,
    value: 202,
    label: "DOC-202 - Dr. Rao (Medicine)",
    data: { code: "202", name: "Dr. Rao (Medicine)", doctorId: 202 },
  },
  {
    id: 203,
    value: 203,
    label: "DOC-203 - Dr. Khan (Surgery)",
    data: { code: "203", name: "Dr. Khan (Surgery)", doctorId: 203 },
  },
];
const OT_ASST_SURGEON_OPTIONS = [
  {
    id: 301,
    value: 301,
    label: "DOC-301 - Dr. Mehta",
    data: { code: "301", name: "Dr. Mehta", doctorId: 301 },
  },
  {
    id: 302,
    value: 302,
    label: "DOC-302 - Dr. Iyer",
    data: { code: "302", name: "Dr. Iyer", doctorId: 302 },
  },
];
const OT_SURGERY_NOTES_TEMPLATE_OPTIONS = [
  {
    id: "snt-1",
    value: "snt-1",
    label: "General Laparotomy",
    data: {
      text:
        "Patient prepared, painted and draped under all aseptic precautions.\n" +
        "Midline incision given. Layer-by-layer dissection done. Findings recorded.\n" +
        "Procedure carried out as planned. Hemostasis achieved.\n" +
        "Closure done in layers. Sterile dressing applied.\n" +
        "Patient shifted to recovery in stable condition.",
    },
  },
  {
    id: "snt-2",
    value: "snt-2",
    label: "Appendectomy (Open)",
    data: {
      text:
        "Right lower quadrant grid-iron incision given.\n" +
        "Appendix identified, mesoappendix ligated and divided.\n" +
        "Base of appendix transfixed and excised.\n" +
        "Stump buried. Hemostasis achieved.\n" +
        "Closure in layers. Sterile dressing applied.",
    },
  },
  {
    id: "snt-3",
    value: "snt-3",
    label: "Hernia Repair",
    data: {
      text:
        "Inguinal incision given. Sac identified, dissected and reduced.\n" +
        "Mesh repair done with prolene mesh, fixed with non-absorbable sutures.\n" +
        "Hemostasis achieved.\n" +
        "Layer-wise closure done. Sterile dressing applied.",
    },
  },
];
const OT_DRAINS_OPTIONS = [
  { value: "rv", label: "RV" },
  { value: "abdominal", label: "Abdominal" },
  { value: "chest", label: "Chest" },
  { value: "corrugated", label: "Corrugated" },
];
const OT_FINAL_PROCEDURE_OPTIONS = [
  { value: "curative", label: "Curative" },
  { value: "palliative", label: "Palliative" },
  { value: "exploratory", label: "Exploratory" },
];
const OT_INTRA_OP_COURSE_OPTIONS = [
  { value: "uneventful", label: "Uneventful" },
  { value: "returned", label: "Returned" },
  { value: "stable", label: "Stable" },
];
const OT_COMPLICATED_OPTIONS = [
  { value: "toIcu", label: "To ICU" },
  { value: "conscious", label: "Conscious" },
];
const OT_VENT_SUPPORT_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "intubated", label: "Intubated" },
];
const OT_RESIDUAL_DISEASE_OPTIONS = [
  { value: "r0", label: "R0" },
  { value: "r1", label: "R1" },
  { value: "r2", label: "R2" },
];

const PATIENT_SOURCE_BTN = {
  opd: {
    selected: "bg-blue-500 text-white border-blue-500 shadow-sm",
    unselected: "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200",
  },
  ipd: {
    selected: "bg-amber-500 text-white border-amber-500 shadow-sm",
    unselected: "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200",
  },
};

function buildPatientSearchOption(patientData, optionId) {
  return {
    id: optionId,
    value: optionId,
    label: `${patientData.name} - ${patientData.uhid} - ${patientData.opdNo}`,
    data: patientData,
  };
}
const OT_STAFF_OPTIONS = [
  {
    id: 401,
    value: 401,
    label: "STF-401 - Sister Anita (Scrub)",
    data: { code: "401", name: "Sister Anita (Scrub)", empId: 401 },
  },
  {
    id: 404,
    value: 404,
    label: "STF-404 - Sister Priya (Circulating)",
    data: { code: "404", name: "Sister Priya (Circulating)", empId: 404 },
  },
];

const OT_INSTRUMENT_OPTIONS = [
  { id: "ins1", value: "ins1", label: "General instrument set", data: { name: "General instrument set" } },
  { id: "ins2", value: "ins2", label: "Laparoscopic tray", data: { name: "Laparoscopic tray" } },
];
const OT_IMPLANT_OPTIONS = [
  { id: "imp1", value: "imp1", label: "Hip stem — standard", data: { name: "Hip stem — standard" } },
  { id: "imp2", value: "imp2", label: "Spinal cage 12mm", data: { name: "Spinal cage 12mm" } },
];
const OT_MEDICINE_OPTIONS = [
  { id: "med1", value: "med1", label: "Inj. Ondansetron 2ml", data: { name: "Inj. Ondansetron 2ml" } },
  { id: "med2", value: "med2", label: "Inj. Cefuroxime 1.5g", data: { name: "Inj. Cefuroxime 1.5g" } },
];
const OT_SERVICE_OPTIONS = [
  { id: "os1", value: "os1", label: "CSSD sterile dispatch", data: { name: "CSSD sterile dispatch" } },
  { id: "os2", value: "os2", label: "Blood bank standby", data: { name: "Blood bank standby" } },
];

const OT_DETAILS_TABS = [
  { id: "sheet", label: "OT Sheet Details" },
  { id: "operation", label: "Operation Details" },
  { id: "items", label: "Instruments & Implants Details" },
  { id: "surgeryNotes", label: "Surgery Notes" },
  { id: "anaesthetistNotes", label: "Anaesthetist Notes" },
  { id: "recovery", label: "Recovery Observations" },
  { id: "attachments", label: "Attachments" },
];

function OtTealAddButton({ onClick, disabled = false, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`h-8 w-full min-h-[2rem] shrink-0 rounded-lg bg-[#217E90] hover:bg-[#1a6575] disabled:opacity-50 disabled:pointer-events-none px-2.5 sm:px-3 text-xs font-bold text-white shadow-sm flex items-center justify-center gap-0.5 ${className}`.trim()}
    >
      <span className="text-sm leading-none font-bold" aria-hidden>
        +
      </span>
      <span>Add</span>
    </button>
  );
}

function OtMiniTrashButton({ onClick, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center justify-center text-red-500 hover:text-red-600"
      aria-label={ariaLabel}
    >
      <TrashIcon className="h-4 w-4" />
    </button>
  );
}

function OtSingleNameTable({ rows, nameHeader, onRemoveRow }) {
  return (
    <div className="rounded border border-gray-200 overflow-hidden bg-white min-h-[6rem]">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            <th className="border-b px-2 py-1 text-center font-medium text-gray-600 w-14">Action</th>
            <th className="border-b px-2 py-1 text-left font-medium text-gray-600">{nameHeader}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={2} className="px-2 py-3 text-center text-gray-400">
                No items added
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={row.id}>
                <td className="border-b border-gray-100 px-2 py-1 text-center">
                  <OtMiniTrashButton ariaLabel="Remove" onClick={() => onRemoveRow(i)} />
                </td>
                <td className="border-b border-gray-100 px-2 py-1">{row.name}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function OtCodeNameTable({ rows, onRemoveRow }) {
  return (
    <div className="rounded border border-gray-200 overflow-hidden bg-white min-h-[6rem]">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50">
          <tr>
            <th className="border-b px-2 py-1 text-center font-medium text-gray-600 w-14">Action</th>
            <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Code</th>
            <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Service Name</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={3} className="px-2 py-3 text-center text-gray-400">
                No services added
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={row.id}>
                <td className="border-b border-gray-100 px-2 py-1 text-center">
                  <OtMiniTrashButton ariaLabel="Remove row" onClick={() => onRemoveRow(i)} />
                </td>
                <td className="border-b border-gray-100 px-2 py-1">{row.code}</td>
                <td className="border-b border-gray-100 px-2 py-1">{row.serviceName}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function OtPickerSection({
  title,
  pickFieldName,
  control,
  dataArray,
  rows,
  onAdd,
  onRemoveRow,
}) {
  return (
    <fieldset className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 pb-3 min-w-0">
      <legend className="px-1.5 text-xs font-semibold text-[#217E90]">{title}</legend>
      <div className="space-y-2 pt-0.5">
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-9">
            <SearchDropdown
              name={pickFieldName}
              control={control}
              dataArray={dataArray}
              label=""
              placeholder={title}
              isSearchable
              isClearable
              selectSize="h-8"
            />
          </div>
          <div className="col-span-3">
            <OtTealAddButton onClick={onAdd} />
          </div>
        </div>
        <OtCodeNameTable rows={rows} onRemoveRow={onRemoveRow} />
      </div>
    </fieldset>
  );
}

function OtInlineCheckboxGroup({ label, options, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    const next = selected.includes(v)
      ? selected.filter((x) => x !== v)
      : [...selected, v];
    onChange(next);
  };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs py-0.5">
      <span className="text-gray-700 font-medium min-w-[8.5rem] shrink-0">{label}</span>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-3 gap-y-2 flex-1">
        {options.map((opt) => (
          <label
            key={opt.value}
            className="inline-flex items-center gap-1.5 cursor-pointer min-w-0"
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-[#217E90] shrink-0"
              checked={selected.includes(opt.value)}
              onChange={() => toggle(opt.value)}
            />
            <span className="text-gray-700 truncate">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function OtInlineRadioGroup({ label, name, options, value, onChange }) {
  const radioName = name || label;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs py-0.5">
      <span className="text-gray-700 font-medium min-w-[8.5rem] shrink-0">{label}</span>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-3 gap-y-2 flex-1">
        {options.map((opt) => (
          <label
            key={opt.value}
            className="inline-flex items-center gap-1.5 cursor-pointer min-w-0"
          >
            <input
              type="radio"
              name={radioName}
              className="h-3.5 w-3.5 accent-[#217E90] shrink-0"
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
            />
            <span className="text-gray-700 truncate">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

const OTDetails = ({ screenTitle = "OT Details" }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const {
    theatres,
    tables,
    surgeries,
    anaesthesiaTypes,
    otResults,
    operationDetailsId,
    isOtReserved,
    saving,
  } = useSelector((state) => state.otNote);

  const defaultValues = {
    otPatientSearch: null,
    theatreName: null,
    tableName: null,
    surgeonName: null,
    anaesthetistName: null,
    surgeryDate: new Date().toISOString().slice(0, 10),
    startTime: "",
    endTime: "",
    durationMin: "",
    inductionTime: "",
    incisionTime: "",
    closerTime: "",
    extubationTime: "",
    otSheetNo: "",
    anaesthesiaType: [],
    otResult: null,
    drains: [],
    finalProcedure: "",
    intraOpCourse: "",
    complicated: [],
    ventSupport: "",
    residualDisease: "",
    tourniquet: false,
    shiftedTo: null,
    remarkSurgeryPlan: "",
    anyComplications: "",
    proDiagnosis: null,
    finDiagnosis: "",
    cdGraphHandover: "",
    implantsDetails: "",
    bloodLossDetails: "",
    surgeryNotesTemplate: null,
    surgeryNotesText: "",
    surgeryNotesIntraOpText: "",
    anaesthetistNotesTemplate: null,
    anaesthetistNotesText: "",
    recoveryTemplate: null,
    recoveryText: "",
    attachmentName: "",
    attachmentPath: "",
    surgeryPick: null,
    asstDocPick: null,
    asstSurgeonPick: null,
    staffPick: null,
    instrumentPick: null,
    implantPick: null,
    medicinePick: null,
    servicePick: null,
  };

  const { control, getValues, setValue, watch, reset } = useForm({ defaultValues });

  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientTypeFilter, setPatientTypeFilter] = useState("opd");
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [opdQueueModalOpen, setOpdQueueModalOpen] = useState(false);
  const [ipdPatientsModalOpen, setIpdPatientsModalOpen] = useState(false);
  const [ipdAdmissionRows, setIpdAdmissionRows] = useState([]);
  const [activeTab, setActiveTab] = useState("sheet");
  const [isUnplannedOt, setIsUnplannedOt] = useState(false);
  const [surgeryList, setSurgeryList] = useState([]);
  const [asstDoctorList, setAsstDoctorList] = useState([]);
  const [asstSurgeonList, setAsstSurgeonList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [instrumentList, setInstrumentList] = useState([]);
  const [implantList, setImplantList] = useState([]);
  const [medicineList, setMedicineList] = useState([]);
  const [serviceList, setServiceList] = useState([]);
  const [attachmentRows, setAttachmentRows] = useState([]);
  const attachmentFileInputRef = useRef(null);
  const watchedTheatre = watch("theatreName");
  const drainsValue = watch("drains") || [];
  const finalProcedureValue = watch("finalProcedure") || "";
  const intraOpCourseValue = watch("intraOpCourse") || "";
  const complicatedValue = watch("complicated") || [];
  const ventSupportValue = watch("ventSupport") || "";
  const residualDiseaseValue = watch("residualDisease") || "";
  const tourniquetValue = !!watch("tourniquet");

  const handleAttachmentBrowseClick = () => {
    attachmentFileInputRef.current?.click();
  };

  const handleAttachmentFileSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const currentName = String(getValues("attachmentName") || "").trim();
    if (!currentName) {
      setValue("attachmentName", file.name, { shouldDirty: true });
    }
    setValue("attachmentPath", file.name, { shouldDirty: true });
    if (event.target) event.target.value = "";
  };

  const handleResetForm = () => {
    reset(defaultValues);
    setActiveTab("sheet");
    setSelectedPatient(null);
    setPatientOptions([]);
    setSurgeryList([]);
    setAsstDoctorList([]);
    setAsstSurgeonList([]);
    setStaffList([]);
    setInstrumentList([]);
    setImplantList([]);
    setMedicineList([]);
    setServiceList([]);
    setAttachmentRows([]);
    dispatch(resetOtNote());
    dispatch(fetchOtLookups());
  };

  const handleAddPickedCodeNameRow = (pickFieldName, setter) => {
    const opt = getValues(pickFieldName);
    const code = String(opt?.data?.code || opt?.id || "").trim();
    const serviceName = String(opt?.data?.name || opt?.label || "").trim();
    if (!code || !serviceName) return;
    setter((prev) => {
      if (prev.some((r) => String(r.code) === String(code))) return prev;
      return [
        ...prev,
        {
          id: `${pickFieldName}-${code}-${Date.now()}`,
          code,
          serviceName,
          surgeryId: opt?.data?.surgeryId ?? opt?.id,
          doctorId: opt?.data?.doctorId ?? opt?.id,
          empId: opt?.data?.empId ?? opt?.id,
        },
      ];
    });
    setValue(pickFieldName, null);
  };

  const handleAddSingleNameRow = (pickFieldName, setter) => {
    const opt = getValues(pickFieldName);
    const name = String(opt?.data?.name || opt?.label || "").trim();
    if (!name) return;
    setter((prev) => {
      if (prev.some((r) => r.name === name)) return prev;
      return [...prev, { id: `${pickFieldName}-${Date.now()}`, name }];
    });
    setValue(pickFieldName, null);
  };

  const handleAddAttachment = () => {
    const name = String(getValues("attachmentName") || "").trim();
    const path = String(getValues("attachmentPath") || "").trim();
    if (!name || !path) return;
    setAttachmentRows((prev) => [...prev, { id: `att-${Date.now()}`, name, path }]);
    setValue("attachmentName", "");
    setValue("attachmentPath", "");
  };

  const tabContent = useMemo(() => {
    if (activeTab === "sheet") {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <InputField name="otSheetNo" control={control} label="OT Sheet No." inputSize="h-8" />
            <CommonDatePicker name="surgeryDate" control={control} label="Surgery Date" />
            <CommonTimePicker
              name="startTime"
              control={control}
              label="Start Time"
              type="time"
            />
            <CommonTimePicker
              name="endTime"
              control={control}
              label="End Time"
              type="time"
            />
            <InputField name="durationMin" control={control} label="Duration (Min.)" inputSize="h-8" />
            <CommonTimePicker
              name="inductionTime"
              control={control}
              label="Induction Time"
              type="time"
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            <CommonTimePicker
              name="incisionTime"
              control={control}
              label="Incision Time"
              type="time"
            />
            <CommonTimePicker
              name="closerTime"
              control={control}
              label="Closer Time"
              type="time"
            />
            <CommonTimePicker
              name="extubationTime"
              control={control}
              label="Extubation Time"
              type="time"
            />
            <SearchDropdown
              name="anaesthesiaType"
              control={control}
              dataArray={anaesthesiaTypes}
              label="Anaesthesia Type"
              isMulti
              isSearchable
              isClearable
              selectSize="h-8"
            />
            <SearchDropdown
              name="otResult"
              control={control}
              dataArray={otResults}
              label="OT Result"
              isSearchable
              isClearable
              selectSize="h-8"
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 space-y-2">
              <OtInlineCheckboxGroup
                label="Drains"
                options={OT_DRAINS_OPTIONS}
                value={drainsValue}
                onChange={(v) => setValue("drains", v, { shouldDirty: true, shouldTouch: true })}
              />
              <OtInlineRadioGroup
                label="Final Procedure"
                name="finalProcedure"
                options={OT_FINAL_PROCEDURE_OPTIONS}
                value={finalProcedureValue}
                onChange={(v) => setValue("finalProcedure", v, { shouldDirty: true, shouldTouch: true })}
              />
              <OtInlineRadioGroup
                label="Intra Op. Course"
                name="intraOpCourse"
                options={OT_INTRA_OP_COURSE_OPTIONS}
                value={intraOpCourseValue}
                onChange={(v) => setValue("intraOpCourse", v, { shouldDirty: true, shouldTouch: true })}
              />
              <OtInlineCheckboxGroup
                label="Complicated"
                options={OT_COMPLICATED_OPTIONS}
                value={complicatedValue}
                onChange={(v) => setValue("complicated", v, { shouldDirty: true, shouldTouch: true })}
              />
              <OtInlineRadioGroup
                label="Vent. Support"
                name="ventSupport"
                options={OT_VENT_SUPPORT_OPTIONS}
                value={ventSupportValue}
                onChange={(v) => setValue("ventSupport", v, { shouldDirty: true, shouldTouch: true })}
              />
              <OtInlineRadioGroup
                label="Residual Disease"
                name="residualDisease"
                options={OT_RESIDUAL_DISEASE_OPTIONS}
                value={residualDiseaseValue}
                onChange={(v) => setValue("residualDisease", v, { shouldDirty: true, shouldTouch: true })}
              />
              <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-[#217E90]"
                  checked={tourniquetValue}
                  onChange={(e) =>
                    setValue("tourniquet", e.target.checked, { shouldDirty: true, shouldTouch: true })
                  }
                />
                <span>Tourniquet</span>
              </label>
            </div>
            <div className="space-y-2">
              <SearchDropdown
                name="shiftedTo"
                control={control}
                dataArray={tables}
                label="Shifted To"
                isSearchable
                isClearable
                selectSize="h-8"
              />
              <InputField name="anyComplications" control={control} label="Any Complications" inputSize="h-8" />
              <SearchDropdown name="proDiagnosis" control={control} dataArray={OT_SERVICE_OPTIONS} label="Pro. Diagnosis" isSearchable isClearable selectSize="h-8" />
              <InputField name="finDiagnosis" control={control} label="Fin. Diagnosis" inputSize="h-8" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <InputAreaField
              name="remarkSurgeryPlan"
              control={control}
              label="Remark / Surgery Plan"
              rows={2}
            />
            <InputAreaField
              name="cdGraphHandover"
              control={control}
              label="CD & Graph Handover"
              rows={2}
            />
            <InputAreaField
              name="implantsDetails"
              control={control}
              label="Implants Details"
              rows={2}
            />
            <InputAreaField
              name="bloodLossDetails"
              control={control}
              label="Blood Loss Details"
              rows={2}
            />
          </div>
        </div>
      );
    }

    if (activeTab === "operation") {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <SearchDropdown name="theatreName" control={control} dataArray={theatres} label="Theatre Name" isSearchable isClearable selectSize="h-8" />
            <SearchDropdown name="tableName" control={control} dataArray={tables} label="Table Name" isSearchable isClearable selectSize="h-8" />
            <SearchDropdown name="surgeonName" control={control} dataArray={OT_SURGEON_OPTIONS} label="Surgeon Name" isSearchable isClearable selectSize="h-8" />
            <SearchDropdown name="anaesthetistName" control={control} dataArray={OT_ANAESTHETIST_OPTIONS} label="Anaesthetist Name" isSearchable isClearable selectSize="h-8" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <OtPickerSection
              title="Surgery Name"
              pickFieldName="surgeryPick"
              control={control}
              dataArray={surgeries}
              rows={surgeryList}
              onAdd={() => handleAddPickedCodeNameRow("surgeryPick", setSurgeryList)}
              onRemoveRow={(i) =>
                setSurgeryList((p) => p.filter((_, idx) => idx !== i))
              }
            />
            <OtPickerSection
              title="Asst Doc. Name"
              pickFieldName="asstDocPick"
              control={control}
              dataArray={OT_ASST_DOC_OPTIONS}
              rows={asstDoctorList}
              onAdd={() => handleAddPickedCodeNameRow("asstDocPick", setAsstDoctorList)}
              onRemoveRow={(i) =>
                setAsstDoctorList((p) => p.filter((_, idx) => idx !== i))
              }
            />
            <OtPickerSection
              title="Asst Surgeon Name"
              pickFieldName="asstSurgeonPick"
              control={control}
              dataArray={OT_ASST_SURGEON_OPTIONS}
              rows={asstSurgeonList}
              onAdd={() =>
                handleAddPickedCodeNameRow("asstSurgeonPick", setAsstSurgeonList)
              }
              onRemoveRow={(i) =>
                setAsstSurgeonList((p) => p.filter((_, idx) => idx !== i))
              }
            />
            <OtPickerSection
              title="Staff Name"
              pickFieldName="staffPick"
              control={control}
              dataArray={OT_STAFF_OPTIONS}
              rows={staffList}
              onAdd={() => handleAddPickedCodeNameRow("staffPick", setStaffList)}
              onRemoveRow={(i) =>
                setStaffList((p) => p.filter((_, idx) => idx !== i))
              }
            />
          </div>
        </div>
      );
    }

    if (activeTab === "items") {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <fieldset className="border border-gray-200 rounded px-2.5 pb-2.5">
              <legend className="px-1 text-xs font-semibold text-[#217E90]">Instruments</legend>
              <div className="pt-1 space-y-2">
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-9">
                    <SearchDropdown name="instrumentPick" control={control} dataArray={OT_INSTRUMENT_OPTIONS} label="" placeholder="Instruments" isSearchable isClearable selectSize="h-8" />
                  </div>
                  <div className="col-span-3">
                    <OtTealAddButton onClick={() => handleAddSingleNameRow("instrumentPick", setInstrumentList)} />
                  </div>
                </div>
                <OtSingleNameTable rows={instrumentList} nameHeader="Instrument Name" onRemoveRow={(i) => setInstrumentList((p) => p.filter((_, idx) => idx !== i))} />
              </div>
            </fieldset>

            <fieldset className="border border-gray-200 rounded px-2.5 pb-2.5">
              <legend className="px-1 text-xs font-semibold text-[#217E90]">Medicines</legend>
              <div className="pt-1 space-y-2">
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-9">
                    <SearchDropdown name="medicinePick" control={control} dataArray={OT_MEDICINE_OPTIONS} label="" placeholder="Medicines" isSearchable isClearable selectSize="h-8" />
                  </div>
                  <div className="col-span-3">
                    <OtTealAddButton onClick={() => handleAddSingleNameRow("medicinePick", setMedicineList)} />
                  </div>
                </div>
                <OtSingleNameTable rows={medicineList} nameHeader="Medicine Name" onRemoveRow={(i) => setMedicineList((p) => p.filter((_, idx) => idx !== i))} />
              </div>
            </fieldset>
          </div>

          <div className="space-y-3">
            <fieldset className="border border-gray-200 rounded px-2.5 pb-2.5">
              <legend className="px-1 text-xs font-semibold text-[#217E90]">Implants</legend>
              <div className="pt-1 space-y-2">
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-9">
                    <SearchDropdown name="implantPick" control={control} dataArray={OT_IMPLANT_OPTIONS} label="" placeholder="Implants" isSearchable isClearable selectSize="h-8" />
                  </div>
                  <div className="col-span-3">
                    <OtTealAddButton onClick={() => handleAddSingleNameRow("implantPick", setImplantList)} />
                  </div>
                </div>
                <OtSingleNameTable rows={implantList} nameHeader="Implant Name" onRemoveRow={(i) => setImplantList((p) => p.filter((_, idx) => idx !== i))} />
              </div>
            </fieldset>

            <fieldset className="border border-gray-200 rounded px-2.5 pb-2.5">
              <legend className="px-1 text-xs font-semibold text-[#217E90]">Services</legend>
              <div className="pt-1 space-y-2">
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-9">
                    <SearchDropdown name="servicePick" control={control} dataArray={OT_SERVICE_OPTIONS} label="" placeholder="Services" isSearchable isClearable selectSize="h-8" />
                  </div>
                  <div className="col-span-3">
                    <OtTealAddButton onClick={() => handleAddSingleNameRow("servicePick", setServiceList)} />
                  </div>
                </div>
                <OtSingleNameTable rows={serviceList} nameHeader="Service Name" onRemoveRow={(i) => setServiceList((p) => p.filter((_, idx) => idx !== i))} />
              </div>
            </fieldset>
          </div>
        </div>
      );
    }

    if (activeTab === "surgeryNotes") {
      const surgeryNotesTemplateText = watch("surgeryNotesText") || "";
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <fieldset className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 pb-3 min-w-0 flex flex-col">
            <legend className="px-1.5 text-xs font-semibold text-[#217E90]">Surgery Notes Template</legend>
            <div className="space-y-2 pt-0.5 flex flex-col flex-1">
              <SearchDropdown
                name="surgeryNotesTemplate"
                control={control}
                dataArray={OT_SURGERY_NOTES_TEMPLATE_OPTIONS}
                label="Template"
                isSearchable
                isClearable
                selectSize="h-8"
                onChange={(_value, option) => {
                  const text = option?.data?.text ?? "";
                  setValue("surgeryNotesText", text, { shouldDirty: true });
                }}
              />
              <textarea
                value={surgeryNotesTemplateText}
                onChange={(e) =>
                  setValue("surgeryNotesText", e.target.value, { shouldDirty: true })
                }
                placeholder="Template description will appear here."
                className="w-full flex-1 min-h-[18rem] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#217E90]"
              />
            </div>
          </fieldset>

          <SpeechToTextArea
            name="surgeryNotesIntraOpText"
            control={control}
            legend="Intra Operative Notes / Post-Operative Order"
            placeholder="Enter intra operative notes / post-operative order."
            rows={14}
          />
        </div>
      );
    }

    if (activeTab === "anaesthetistNotes") {
      return (
        <div className="space-y-3">
          <div className="w-full sm:w-[45%]">
            <SearchDropdown
              name="anaesthetistNotesTemplate"
              control={control}
              dataArray={OT_SERVICE_OPTIONS}
              label="Anaesthetist Notes Templates"
              isSearchable
              isClearable
              selectSize="h-8"
            />
          </div>
          <SpeechToTextArea
            name="anaesthetistNotesText"
            control={control}
            legend="Anaesthetist Notes"
            placeholder="Enter anaesthetist notes."
            rows={14}
            fieldsetClassName="min-w-0 flex flex-col"
          />
        </div>
      );
    }

    if (activeTab === "recovery") {
      return (
        <div className="space-y-3">
          <div className="w-full sm:w-[45%]">
            <SearchDropdown
              name="recoveryTemplate"
              control={control}
              dataArray={OT_SERVICE_OPTIONS}
              label="Recovery Observations Templates"
              isSearchable
              isClearable
              selectSize="h-8"
            />
          </div>
          <SpeechToTextArea
            name="recoveryText"
            control={control}
            legend="Recovery Observations"
            placeholder="Enter recovery observations."
            rows={14}
            fieldsetClassName="min-w-0 flex flex-col"
          />
        </div>
      );
    }

    if (activeTab === "attachments") {
      return (
        <div className="space-y-3">
          <input
            ref={attachmentFileInputRef}
            type="file"
            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
            className="hidden"
            onChange={handleAttachmentFileSelected}
          />

          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
            <div className="md:col-span-4">
              <InputField name="attachmentName" control={control} label="File Name" inputSize="h-8" />
            </div>
            <div className="md:col-span-6">
              <InputField name="attachmentPath" control={control} label="File Path" inputSize="h-8" />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button
                type="button"
                onClick={handleAttachmentBrowseClick}
                title="Choose file or photo"
                aria-label="Choose file or photo"
                className="h-8 w-12 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-[#217E90] hover:bg-[#E8FBF6] focus:outline-none focus:ring-1 focus:ring-[#217E90]"
              >
                <PaperClipIcon className="h-4 w-4" />
              </button>
              <CommonButton label="Add" type="button" variant="reset-outlined" onClick={handleAddAttachment} className="h-8 flex-1 text-xs border border-sky-500 !text-red-600" />
            </div>
          </div>

          <div className="rounded border border-gray-200 overflow-hidden bg-white min-h-[12rem]">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b px-2 py-1 text-left font-medium text-gray-600">File Name</th>
                  <th className="border-b px-2 py-1 text-left font-medium text-gray-600">File Location</th>
                  <th className="border-b px-2 py-1 text-center font-medium text-gray-600 w-14">Action</th>
                </tr>
              </thead>
              <tbody>
                {attachmentRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-2 py-6 text-center text-gray-400">
                      No attachments
                    </td>
                  </tr>
                ) : (
                  attachmentRows.map((r, i) => (
                    <tr key={r.id}>
                      <td className="border-b border-gray-100 px-2 py-1">{r.name}</td>
                      <td className="border-b border-gray-100 px-2 py-1">{r.path}</td>
                      <td className="border-b border-gray-100 px-2 py-1 text-center">
                        <OtMiniTrashButton ariaLabel="Remove attachment" onClick={() => setAttachmentRows((p) => p.filter((_, idx) => idx !== i))} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return null;
  }, [
    activeTab,
    control,
    getValues,
    setValue,
    surgeryList,
    asstDoctorList,
    asstSurgeonList,
    staffList,
    instrumentList,
    implantList,
    medicineList,
    serviceList,
    attachmentRows,
    anaesthesiaTypes,
    otResults,
    theatres,
    tables,
    surgeries,
    drainsValue,
    finalProcedureValue,
    intraOpCourseValue,
    complicatedValue,
    ventSupportValue,
    residualDiseaseValue,
    tourniquetValue,
  ]);

  const loadExistingNoteForVisit = useCallback(
    async (patientData) => {
      if (!patientData?.opdIpdId) return;
      const listResult = await dispatch(
        fetchOtNotesForVisit({
          opdIpdId: patientData.opdIpdId,
          opdIpd: Boolean(patientData.opdIpd),
        })
      );
      if (fetchOtNotesForVisit.rejected.match(listResult)) {
        return;
      }
      const notes = listResult.payload || [];
      if (!notes.length) {
        dispatch(setOperationDetailsId(null));
        return;
      }
      const latest = notes[0];
      const detailResult = await dispatch(fetchOtNoteById(latest.operationDetailsId));
      if (fetchOtNoteById.fulfilled.match(detailResult)) {
        applyOtNoteDetailToForm({
          detail: detailResult.payload,
          setValue,
          setSurgeryList,
          setAsstDoctorList,
          setAsstSurgeonList,
          setStaffList,
          theatreOptions: theatres,
          tableOptions: tables,
          anaesthesiaOptions: anaesthesiaTypes,
          otResultOptions: otResults,
          surgeonOptions: OT_SURGEON_OPTIONS,
          anaesthetistOptions: OT_ANAESTHETIST_OPTIONS,
        });
        if (detailResult.payload?.note?.otId) {
          dispatch(fetchOtTables(detailResult.payload.note.otId));
        }
      }
    },
    [dispatch, setValue, theatres, tables, anaesthesiaTypes, otResults]
  );

  const handlePatientSearchInputChange = (value) => {
    if (!value || String(value).trim().length < 1) return;
    const q = String(value).trim().toLowerCase();
    setPatientOptions((prev) =>
      prev.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          String(o.data?.name || "").toLowerCase().includes(q) ||
          String(o.data?.uhid || "").toLowerCase().includes(q) ||
          String(o.data?.opdNo || "").toLowerCase().includes(q)
      )
    );
  };

  const handlePatientSelect = (option) => {
    const patientData = option?.data || null;
    // Clearing the patient dropdown should wipe the whole OT note form.
    if (!option || !patientData) {
      handleResetForm();
      return;
    }
    setSelectedPatient(patientData);
    dispatch(setSelectedPatientInStore(patientData));
    if (patientData?.opdIpdId != null) {
      loadExistingNoteForVisit(patientData);
    }
  };

  useEffect(() => {
    dispatch(fetchOtLookups());
    setReservationModalOpen(true);
  }, [dispatch]);

  useEffect(() => {
    const otId = watchedTheatre?.id ?? watchedTheatre?.value ?? null;
    if (otId) {
      dispatch(fetchOtTables(otId));
    }
  }, [watchedTheatre, dispatch]);

  const patchPatientToForm = (patientData, optionId) => {
    if (!patientData) return;
    setSelectedPatient(patientData);
    dispatch(setSelectedPatientInStore(patientData));
    const option = buildPatientSearchOption(patientData, optionId);
    setPatientOptions((prev) => {
      if (prev.some((p) => p.id === option.id)) return prev;
      return [option, ...prev];
    });
    setValue("otPatientSearch", option);
    loadExistingNoteForVisit(patientData);
  };

  const handleReservationSelect = (row) => {
    if (!row) return;
    setPatientTypeFilter(row.opdIpd ? "ipd" : "opd");
    const patientData = {
      name: row.patientName,
      uhid: row.uhid,
      opdNo: row.opdNo,
      doctorName: row.doctorName,
      patientType: row.patientType || (row.opdIpd ? "IPD" : "OPD"),
      tariffName: row.tariffName || "—",
      companyName: row.companyName || "—",
      regId: row.regId,
      opdIpdId: row.opdIpdId,
      opdIpd: Boolean(row.opdIpd),
      otReservationId: row.otReservationId || row.reservationId,
    };
    dispatch(
      setOtReservationContext({
        otReservationId: patientData.otReservationId,
        isOtReserved: true,
      })
    );
    patchPatientToForm(patientData, `res-${patientData.otReservationId}`);
    if (row.surgeryDate) setValue("surgeryDate", row.surgeryDate);
    if (row.surgeryStartTime) setValue("startTime", formatDisplayTime(row.surgeryStartTime));
    if (row.surgeryEndTime) setValue("endTime", formatDisplayTime(row.surgeryEndTime));
    if (row.duration != null) setValue("durationMin", String(row.duration));
    const theatre =
      theatres.find((o) => Number(o.id) === Number(row.otId || row.theatreId)) ||
      (row.otId || row.theatreId
        ? toLookupOption({
            id: row.otId || row.theatreId,
            name: row.theatreName || String(row.otId || row.theatreId),
          })
        : null);
    if (theatre) {
      setValue("theatreName", theatre);
      dispatch(fetchOtTables(theatre.id));
    }
    const table =
      tables.find((o) => Number(o.id) === Number(row.otTableId || row.tableId)) ||
      (row.otTableId || row.tableId
        ? toLookupOption({
            id: row.otTableId || row.tableId,
            name: row.tableName || String(row.otTableId || row.tableId),
          })
        : null);
    if (table) setValue("tableName", table);
    if (row.surgeryId) {
      setSurgeryList([
        {
          id: `surg-${row.surgeryId}`,
          code: String(row.surgeryId),
          serviceName: row.surgeryName || String(row.surgeryId),
          surgeryId: row.surgeryId,
        },
      ]);
    }
    if (row.doctorId) {
      const surgeon =
        OT_SURGEON_OPTIONS.find((o) => Number(o.id) === Number(row.doctorId)) || {
          id: row.doctorId,
          value: row.doctorId,
          label: row.doctorName || String(row.doctorId),
        };
      setValue("surgeonName", surgeon);
    }
  };

  const handleOpdQueueSelect = (row) => {
    if (!row) return;
    setPatientTypeFilter("opd");
    dispatch(setOtReservationContext({ otReservationId: null, isOtReserved: false }));
    patchPatientToForm(
      {
        name: row.patientName,
        uhid: row.uhid,
        opdNo: row.visitNo,
        doctorName: row.doctor,
        patientType: "OPD",
        tariffName: row.tariffName || "—",
        companyName: row.companyName || "—",
        regId: row.regId,
        opdIpdId: row.opdIpdId || row.visitId,
        opdIpd: false,
      },
      `opd-${row.visitId}`
    );
  };

  const handleIpdPatientSelect = (row) => {
    if (!row) return;
    setPatientTypeFilter("ipd");
    dispatch(setOtReservationContext({ otReservationId: null, isOtReserved: false }));
    patchPatientToForm(
      {
        name: row.patientName,
        uhid: row.uhid,
        opdNo: row.ipOpNumber || (row.admissionId ? `IP/${row.admissionId}` : "—"),
        doctorName: row.doctorName,
        patientType: "IPD",
        tariffName: row.tariffName || "—",
        companyName: row.refDocName || "—",
        regId: row.regId,
        opdIpdId: row.admissionId,
        opdIpd: true,
      },
      `ipd-${row.admissionId}`
    );
  };

  const loadIpdPatients = useCallback(async () => {
    try {
      const rows = await listAdmittedPatientsForDoctorRound({ isDischarged: false });
      setIpdAdmissionRows((rows || []).map(mapWebPatientToAdmissionRow));
    } catch (error) {
      setIpdAdmissionRows([]);
      showErrorToast(error.message || "Failed to load admitted patients");
    }
  }, []);

  useEffect(() => {
    if (ipdPatientsModalOpen) {
      loadIpdPatients();
    }
  }, [ipdPatientsModalOpen, loadIpdPatients]);

  const handleSaveClick = async () => {
    if (!selectedPatient?.regId || !selectedPatient?.opdIpdId) {
      showErrorToast("Select a patient (reservation / OPD / IPD) before saving");
      return;
    }
    const payload = buildOtNoteSavePayload({
      formValues: getValues(),
      selectedPatient,
      surgeryList,
      asstDoctorList,
      asstSurgeonList,
      staffList,
      userId: authUser?.userId || 1,
      isOtReserved,
    });
    const result = await dispatch(
      saveOtNote({
        operationDetailsId,
        payload,
      })
    );
    if (saveOtNote.fulfilled.match(result)) {
      showSuccessToast(
        operationDetailsId ? "OT note updated successfully" : "OT note saved successfully"
      );
      if (result.payload?.operationDetailsId) {
        dispatch(setOperationDetailsId(result.payload.operationDetailsId));
      }
    } else {
      showErrorToast(result.payload || "Failed to save OT note");
    }
  };

  return (
    <div className="p-1.5 sm:p-2 space-y-2 sm:space-y-3">
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="sticky top-0 z-10 bg-white rounded-t-lg px-2 py-1 sm:px-3 sm:py-1.5 border-b border-gray-200 bg-gradient-to-r shadow-sm">
          <h2 className="text-sm sm:text-base font-semibold text-[#217E90] flex items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-0.5 rounded hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-[#217E90]"
              aria-label="Go back"
            >
              <ArrowUturnLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            </button>
            <span className="truncate">{screenTitle}</span>
          </h2>
        </div>

        <div className="p-2 sm:p-3 space-y-2 sm:space-y-3">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="w-full sm:w-[50%] flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3">
              <div className="flex-1">
                <SearchDropdown
                  name="otPatientSearch"
                  control={control}
                  dataArray={patientOptions}
                  onChange={handlePatientSelect}
                  onInputChange={handlePatientSearchInputChange}
                  label="Search by patient name / UHID / OPD no."
                  isSearchable
                  isClearable
                  selectSize="h-8"
                />
              </div>
              <div className="flex items-center gap-2 px-1 sm:pb-1">
                <button
                  type="button"
                  className={`whitespace-nowrap px-3 py-1.5 text-xs rounded-md transition-all duration-200 flex-shrink-0 border font-medium ${
                    patientTypeFilter === "opd"
                      ? PATIENT_SOURCE_BTN.opd.selected
                      : PATIENT_SOURCE_BTN.opd.unselected
                  }`}
                  onClick={() => {
                    setPatientTypeFilter("opd");
                    setOpdQueueModalOpen(true);
                  }}
                >
                  OPD
                </button>
                <button
                  type="button"
                  className={`whitespace-nowrap px-3 py-1.5 text-xs rounded-md transition-all duration-200 flex-shrink-0 border font-medium ${
                    patientTypeFilter === "ipd"
                      ? PATIENT_SOURCE_BTN.ipd.selected
                      : PATIENT_SOURCE_BTN.ipd.unselected
                  }`}
                  onClick={() => {
                    setPatientTypeFilter("ipd");
                    setIpdPatientsModalOpen(true);
                  }}
                >
                  IPD
                </button>
              </div>
            </div>
          </div>

          <div className="bg-gray-100 rounded-lg p-3 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                  <span className="text-lg font-semibold text-gray-600">
                    {selectedPatient?.name?.trim()?.charAt(0) || "P"}
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0 text-xs sm:text-sm">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1.5">
                  <div className="flex flex-col gap-1">
                    <span className="text-[#217E90] font-semibold truncate">{selectedPatient?.name || "—"}</span>
                    <span className="flex items-baseline text-[11px] min-w-0 gap-1.5">
                      <span className="text-gray-500 shrink-0 min-w-[5.5rem]">Doctor name</span>
                      <span className="text-gray-500 shrink-0">:</span>
                      <span className="text-[#217E90] font-semibold text-xs sm:text-sm truncate">
                        {selectedPatient?.doctorName || "—"}
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="flex items-baseline text-[11px] gap-1.5">
                      <span className="text-gray-500 min-w-[5.5rem] shrink-0">UHID</span>
                      <span className="text-gray-500 shrink-0">:</span>
                      <span className="text-[#217E90] font-semibold text-xs sm:text-sm">{selectedPatient?.uhid || "—"}</span>
                    </span>
                    <span className="flex items-baseline text-[11px] gap-1.5">
                      <span className="text-gray-500 min-w-[5.5rem] shrink-0">OPD No.</span>
                      <span className="text-gray-500 shrink-0">:</span>
                      <span className="text-[#217E90] font-semibold text-xs sm:text-sm">{selectedPatient?.opdNo || "—"}</span>
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="flex items-baseline text-[11px] min-w-0 gap-1.5">
                      <span className="text-gray-500 shrink-0 min-w-[5.5rem]">Tariff</span>
                      <span className="text-gray-500 shrink-0">:</span>
                      <span className="text-[#217E90] font-semibold text-xs sm:text-sm truncate">
                        {selectedPatient?.tariffName || "—"}
                      </span>
                    </span>
                    <span className="flex items-baseline text-[11px] min-w-0 gap-1.5">
                      <span className="text-gray-500 shrink-0 min-w-[5.5rem]">Patient type</span>
                      <span className="text-gray-500 shrink-0">:</span>
                      <span className="text-[#217E90] font-semibold text-xs sm:text-sm truncate">
                        {selectedPatient?.patientType || "—"}
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="flex items-baseline text-[11px] min-w-0 gap-1.5">
                      <span className="text-gray-500 shrink-0 min-w-[5.5rem]">Company</span>
                      <span className="text-gray-500 shrink-0">:</span>
                      <span className="text-[#217E90] font-semibold text-xs sm:text-sm truncate">
                        {selectedPatient?.companyName || "—"}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded border border-gray-200 bg-white">
            <div className="flex items-center justify-between gap-2 border-b border-gray-200 pr-3">
              <div className="flex flex-wrap gap-0">
                {OT_DETAILS_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    className={`px-3 py-2 text-xs sm:text-sm font-semibold transition-colors border-b-2 -mb-px ${
                      activeTab === t.id
                        ? "text-[#217E90] border-[#217E90] bg-white"
                        : "text-gray-500 border-transparent hover:text-gray-700"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-[#217E90]"
                  checked={isUnplannedOt}
                  onChange={(e) => setIsUnplannedOt(e.target.checked)}
                />
                <span>Unplanned OT</span>
              </label>
            </div>
            <div className="p-3 space-y-3">
              {tabContent}
              <div className="flex items-center justify-end gap-2 pt-2">
                <PrintButton
                  type="button"
                  onClick={() => window.print()}
                  size="sm"
                  className="h-8 !px-5 text-xs"
                />
                <CommonButton
                  label="Reset"
                  type="button"
                  variant="danger-outline"
                  onClick={handleResetForm}
                  className="h-8 !px-5 text-xs"
                />
                <CommonButton
                  label={saving ? "Saving…" : "Save"}
                  type="button"
                  variant="primary-filled"
                  onClick={handleSaveClick}
                  disabled={saving}
                  className="h-8 !px-5 text-xs bg-[#217E90] hover:bg-[#1a6575]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <OTReservationListingModal
        isOpen={reservationModalOpen}
        onClose={() => setReservationModalOpen(false)}
        onSelectPatient={handleReservationSelect}
      />
      <OpdQueueListingModal
        isOpen={opdQueueModalOpen}
        onClose={() => setOpdQueueModalOpen(false)}
        onSelectPatient={handleOpdQueueSelect}
      />
      <AdmittedPatientsModal
        isOpen={ipdPatientsModalOpen}
        onClose={() => setIpdPatientsModalOpen(false)}
        onSelectPatient={handleIpdPatientSelect}
        onRowDoubleClick={handleIpdPatientSelect}
        rows={ipdAdmissionRows}
      />
    </div>
  );
};

export default OTDetails;
